import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Grade 8 geometry.
 *
 * Transformations recast congruence as *motion* rather than as a checklist of
 * equal parts, which is the modern treatment and the one that makes similarity
 * fall out for free. Angle relationships then collect what follows from two
 * lines never meeting — including the triangle angle sum, which Grade 7 was
 * told and this lesson explains — and end at AA, the criterion that says
 * angles fix a shape and nothing more. The round solids close the volume
 * thread that began with a box in Grade 6.
 *
 * The formal, proof-based treatment of the same configuration lives at Grade
 * 10 in `geometry-proof.ts`; `parallel-lines` names this concept as a
 * prerequisite, so the two are one idea met twice rather than twice over.
 */
export const GEOMETRY_EIGHT_LESSONS: Lesson[] = [
  lesson({
    id: 'transformations-01',
    unitId: 'unit-transformations',
    conceptId: 'transformations',
    title: 'Transformations and Congruence',
    summary: 'Sliding, turning, flipping and resizing — and what each one leaves unchanged.',
    objectives: [
      'Translate, reflect and rotate a figure on the coordinate plane',
      'Distinguish congruence from similarity',
      'Say what each transformation preserves and what it changes',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Moving a shape without changing it',
        blocks: [
          text('Slide a book across a table. Turn it. Hold it up to a mirror. In each case it is still the same book — same size, same shape, same everything except where it is and which way it faces.'),
          text('Those three moves are **translation**, **rotation** and **reflection**, and they are called *rigid* because they change position without changing the figure. A fourth move, **dilation**, resizes it — and that one is not rigid.'),
        ],
      },
      intuition: {
        title: 'What each move preserves',
        blocks: [
          text('The useful way to hold these is by what survives them.'),
          table(['Transformation', 'What it does', 'Lengths', 'Angles'], [
            ['Translation', 'slides', 'same', 'same'],
            ['Rotation', 'turns about a point', 'same', 'same'],
            ['Reflection', 'flips across a line', 'same', 'same'],
            ['Dilation', 'resizes from a point', 'scaled', 'same'],
          ]),
          insight('The first three preserve everything, so the image is **congruent** to the original — identical in shape and size. A dilation preserves angles but not lengths, so the image is **similar**: the same shape at a different size.'),
          warning('A dilated figure looks unchanged, which is exactly why it gets called congruent. Congruent means you could lay one exactly on top of the other. If the sizes differ, they are similar and nothing more.', 'Similar is not congruent'),
          text('On coordinates the rules are short:'),
          list([
            'Translate by (a, b): every point (x, y) becomes (x + a, y + b).',
            'Reflect in the x-axis: (x, y) becomes (x, −y). In the y-axis: (x, y) becomes (−x, y).',
            'Rotate 180° about the origin: (x, y) becomes (−x, −y).',
            'Dilate by k from the origin: (x, y) becomes (kx, ky).',
          ]),
          insight('This gives congruence a definition worth having: two figures are congruent when *some sequence of rigid motions* carries one onto the other. Not a list of equal sides and angles to check, but a movement you could actually perform.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -8,
              max: 8,
              points: [
                { x: 2, y: 3, label: 'A' },
                { x: 6, y: 3, label: 'B' },
                { x: 2, y: -3, label: "A'" },
                { x: 6, y: -3, label: "B'" },
              ],
            },
            'A and B reflected in the x-axis. The x-coordinates are untouched; the y-coordinates change sign, and the distance between the points is unchanged.',
          ),
          tip('When a transformation is described in words, do it to one point first and check the result looks right on the grid. Getting one point wrong is much easier to spot than getting a whole shape wrong.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A triangle has vertices at (1, 2), (4, 2) and (1, 6). It is translated 3 left and 5 down, then reflected in the y-axis. Where does the first vertex end up?'),
          steps([
            { label: 'Translate: subtract 3 from x, subtract 5 from y.', latex: '(1 - 3,\\ 2 - 5) = (-2, -3)' },
            { label: 'Reflect in the y-axis: change the sign of x.', latex: '(2, -3)' },
            { label: 'Both moves were rigid, so the triangle is unchanged in size.', latex: '\\text{image} \\cong \\text{original}' },
          ]),
          text('Order matters here. Reflecting first and then translating would land somewhere else — these operations do not commute, which is worth noticing now and will matter a great deal later.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('transformations', 4)] },
      independent: { title: 'On your own', blocks: [practice('transformations', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('transformations', 3, 'Telling congruence from similarity.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Translations, rotations and reflections are rigid: they preserve both lengths and angles.',
            'Figures related by rigid motions are congruent.',
            'A dilation preserves angles but scales lengths, giving similar figures.',
            'Reflecting in the x-axis negates y; reflecting in the y-axis negates x.',
            'The order of two transformations changes the result.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'angle-relationships-01',
    unitId: 'unit-angle-geometry',
    conceptId: 'angle-relationships',
    title: 'Angles, Parallel Lines and Similar Triangles',
    summary:
      'Why a transversal makes only two different angles, why every triangle sums to 180°, and why two equal angles are enough to fix a shape.',
    objectives: [
      'Find an angle made by a transversal crossing parallel lines',
      'Explain the triangle angle sum from parallel lines',
      'Use equal angles to establish similarity and find a missing side',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Eight angles, two sizes',
        blocks: [
          text('Draw two parallel lines. Draw a third line crossing both — a **transversal**. It makes eight angles, which looks like eight things to work out.'),
          text('It is two. Every one of the eight is either equal to the angle you were given or makes a straight line with it, so the eight angles take only two different values, and those two add to 180°.'),
          insight('The reason is that the two crossings are *the same crossing*. Slide one along the transversal onto the other and it lands exactly on top, because the lines never converge. Nothing about the picture changed, so nothing about the angles can have.'),
        ],
      },
      intuition: {
        title: 'The three names, and what they are for',
        blocks: [
          text('The angle pairs have names, and the names describe positions rather than sizes — which is the whole difficulty, because you have to read the picture to use them.'),
          table(['Pair', 'Where they sit', 'Relationship'], [
            ['Corresponding', 'the same corner at each crossing', 'equal'],
            ['Alternate', 'between the lines, opposite sides of the transversal', 'equal'],
            ['Co-interior', 'between the lines, the same side of the transversal', 'add to 180°'],
          ]),
          warning('Co-interior is the one that catches people, because two of the three names mean "equal" and the reflex is to answer with the number you were given. Look at which side of the transversal the angles are on: same side means they make a straight line together, so subtract from 180°.', 'The odd one out'),
          text('Now the pay-off. Take any triangle and draw a line through its apex parallel to the base.'),
          insight('The two base angles reappear at the apex as alternate angles, and the three angles there sit along a straight line. So the angles of *any* triangle add to 180° — a fact you were told in Grade 7 and can now say why.'),
          math('a + b + c = 180^{\\circ}'),
          text('And an immediate consequence: an **exterior angle** of a triangle equals the two interior angles it does not touch, because both it and the third angle complete the same straight line.'),
          text('The angle sum also does something less obvious. Fix two angles of a triangle and the third has no choice. So two triangles with two matching angles have all three matching — and that, in Grade 8 terms, is what a dilation does:'),
          insight('**AA is enough for similarity.** If two angles of one triangle equal two of another, the triangles are **similar**: the same shape, at some scale factor k, with every pair of corresponding sides in the same ratio. Angles fix the shape; they say nothing about the size.'),
          warning('Equal angles do not make triangles congruent. A photograph and its enlargement have identical angles and are not the same size. Similar is as much as angles alone can ever give you.', 'Similar, not congruent'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'parallel-lines', mark: [1, 5] },
            'The marked pair sit in the same corner at each crossing — corresponding angles, and equal.',
          ),
          figure(
            { kind: 'parallel-lines', mark: [3, 6] },
            'This pair lies between the parallels on the same side of the transversal — co-interior, and adding to 180° rather than matching.',
          ),
          tip('Two shapes are worth learning to spot. Corresponding angles make an F; alternate angles make a Z. The letter may be backwards or lying down, and it is still the same pair.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Two triangles share an angle of 40°, and each has a second angle of 65°. The first has a side of 6 cm; the corresponding side of the second is 15 cm. Find the third angle, and the scale factor.'),
          steps([
            { label: 'Two angles match, so the triangles are similar.', latex: '40^{\\circ},\\ 65^{\\circ} \\Rightarrow \\text{AA}' },
            { label: 'The angles of a triangle add to 180°.', latex: '180 - 40 - 65 = 75^{\\circ}' },
            { label: 'The scale factor is the ratio of corresponding sides.', latex: 'k = \\tfrac{15}{6} = 2.5' },
            { label: 'Every other side of the second triangle is 2.5 times its partner.', latex: '\\times 2.5' },
          ]),
          text('Check the direction before using k. Going from the small triangle to the large one multiplies; the other way divides, and the answer should come out larger or smaller accordingly.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('angle-relationships', 4)] },
      independent: { title: 'On your own', blocks: [practice('angle-relationships', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('angle-relationships', 3, 'Co-interior angles, and scale factors that go the other way.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A transversal across parallel lines makes only two different angles, adding to 180°.',
            'Corresponding and alternate angles are equal; co-interior angles are supplementary.',
            'The angles of any triangle add to 180°, and parallel lines are why.',
            'An exterior angle equals the two interior angles it does not touch.',
            'Two equal angles make triangles similar — the same shape at some scale factor, not the same size.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'round-solids-01',
    unitId: 'unit-transformations',
    conceptId: 'round-solids',
    title: 'Volume of Cylinders, Cones and Spheres',
    summary: 'Three round solids whose volumes stand in one of the tidiest relationships in geometry.',
    objectives: [
      'Find the volume of a cylinder, a cone and a sphere',
      'Explain why a cone is a third of its cylinder',
      'Choose the right formula from the shape described',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'A circle given a height',
        blocks: [
          text('You already know the volume of a box: work out the area of the base, then multiply by the height. A cylinder is the same idea with a round base.'),
          math('V = \\pi r^{2} h'),
          text('Everything in this lesson follows from that one formula, including the two that look like they need memorising.'),
        ],
      },
      intuition: {
        title: 'The relationship between the three',
        blocks: [
          text('Take a cylinder. Now take a cone with exactly the same base and the same height, and pour the cone into the cylinder.'),
          insight('It fills exactly one third. Not approximately — exactly, for every radius and every height. That is why the cone formula is what it is:'),
          math('V_{\\text{cone}} = \\tfrac{1}{3}\\pi r^{2} h'),
          warning('Forgetting the third is the commonest error here, and it gives the volume of the cylinder around the cone instead — always three times too big.', 'The missing third'),
          text('Now put a sphere inside the smallest cylinder that contains it — one whose height equals the diameter, so h = 2r. The sphere fills exactly two thirds of it:'),
          math('V_{\\text{sphere}} = \\tfrac{2}{3} \\times \\pi r^{2}(2r) = \\tfrac{4}{3}\\pi r^{3}'),
          insight('So a sphere is two thirds of its cylinder, and a cone is one third of its cylinder. Archimedes was proud enough of the sphere result to have it carved on his tombstone, and he found it without calculus.'),
          text('One check applies to all three. A volume fills three dimensions, so the lengths in the formula must multiply to three of them: r²h has two rs and an h, and r³ has three rs. If your formula only reaches two, you have computed an area.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'solid', solid: 'cylinder', labels: { radius: 'r', height: 'h' } },
            'A cylinder: the circular base πr², stacked h high.',
          ),
          figure(
            { kind: 'solid', solid: 'cone', labels: { radius: 'r', height: 'h' } },
            'A cone with the same base and height holds exactly a third as much.',
          ),
          table(['Solid', 'Volume', 'Compared with its cylinder'], [
            ['Cylinder', 'πr²h', '1'],
            ['Cone', '⅓πr²h', 'one third'],
            ['Sphere', '⁴⁄₃πr³', 'two thirds'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A cone has a radius of 6 cm and a height of 9 cm. Find its volume, using π = 3.14. Then find the volume of a sphere with the same radius.'),
          steps([
            { label: 'Start with the cylinder around it.', latex: '\\pi r^{2} h = 3.14 \\times 36 \\times 9 = 1017.36' },
            { label: 'A cone is one third.', latex: '1017.36 \\div 3 = 339.12\\text{ cm}^{3}' },
            { label: 'For the sphere, cube the radius.', latex: '6^{3} = 216' },
            { label: 'Multiply by ⁴⁄₃π.', latex: '\\tfrac{4}{3} \\times 3.14 \\times 216 = 904.32\\text{ cm}^{3}' },
          ]),
          tip('Check the units. All three answers are in cm³ because three lengths were multiplied. An answer in cm² means a radius was used one time too few.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('round-solids', 4)] },
      independent: { title: 'On your own', blocks: [practice('round-solids', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('round-solids', 3, 'Cones and spheres, where the fraction and the power both matter.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Cylinder: V = πr²h — the area of the base times the height.',
            'Cone: V = ⅓πr²h — exactly a third of its cylinder.',
            'Sphere: V = ⁴⁄₃πr³ — exactly two thirds of its cylinder.',
            'A volume multiplies three lengths, so the answer is always in cubic units.',
          ]),
        ],
      },
    },
  }),
];
